import { AxiosInstance } from "axios";
import { ApiResponse } from "./auth";

export interface CaseImage {
  url: string;
  _id?: string;
}

export interface PredictedDiagnosis {
  label: string;
  confidence: number;
  reasoning: string;
  _id?: string;
  isRecommendations?: boolean;
}

export interface Case {
  _id: string;
  images: CaseImage[];
  age: number;
  sex: 'Male' | 'Female';
  doctorDiagnosis: string;
  doctorNote: string;
  patientHistory?: string;
  symptoms?: string[];
  diagnosisStatus: 'pending' | 'processing' | 'ready' | 'failed';
  predictedDiagnoses?: PredictedDiagnosis[];
  createdBy: string | { _id: string; name: string; email: string };
  reviewedBy?: string | { _id: string; name: string; email: string };
  reviewNote?: string;
  reviewStatus?: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

export interface CreateCaseResponse {
  caseId: string;
  jobId: string;
  diagnosisStatus: string;
  case: Case;
}

export interface DiagnosisJobResult {
  jobId: string;
  jobStatus: 'active' | 'completed' | 'failed';
  progress: number;
  caseStatus: 'processing' | 'ready' | 'failed';
  predictedDiagnoses?: PredictedDiagnosis[];
  case?: Case;
  completedAt?: string;
  processingTime?: string;
  estimatedCompletionTime?: string;
}

export interface PaginatedCases {
  cases: Case[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCases: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export class CasesApi {
  constructor(private api: AxiosInstance) {}

  // Create new case with AI diagnosis
  async createCase(caseData: {
    images: CaseImage[];
    age: number;
    sex: 'Male' | 'Female';
    doctorDiagnosis: string;
    doctorNote: string;
    patientHistory?: string;
    symptoms?: string[];
  }): Promise<any> {
    try {
      const response = await this.api.post('/cases', caseData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to create case');
    }
  }

  // Get AI diagnosis result by job ID
  async getDiagnosisResult(jobId: string): Promise<any> {
    try {
      const response = await this.api.get(`/cases/diagnosis/${jobId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get diagnosis result');
    }
  }

  // Poll for diagnosis completion
  async pollDiagnosis(jobId: string, maxAttempts: number = 30, interval: number = 2000): Promise<any> {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const result = await this.getDiagnosisResult(jobId);
        
        if (result.jobStatus === 'completed') {
          return result;
        }
        
        if (result.jobStatus === 'failed') {
          throw new Error('AI diagnosis failed');
        }
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, interval));
        attempts++;
      } catch (error: any) {
        console.error('Diagnosis polling error:', error);
        if (attempts >= maxAttempts - 1) {
          throw error;
        }
        attempts++;
      }
    }
    
    throw new Error('Diagnosis polling timeout');
  }

  // Get my cases with pagination and filters
  async getMyCases(params?: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'processing' | 'ready' | 'failed';
    sortBy?: 'createdAt' | 'updatedAt' | 'diagnosisStatus';
    sortOrder?: 'asc' | 'desc';
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status) queryParams.append('status', params.status);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const response = await this.api.get(`/cases/my-cases?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get cases');
    }
  }

  // Get case by ID
  async getCaseById(caseId: string): Promise<ApiResponse<Case>> {
    try {
      const response = await this.api.get(`/cases/${caseId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get case');
    }
  }

  // Legacy method for analyze case (now handled by createCase + polling)
  async analyzeCase(data: {
    imageUrls: string[];
    clinicalInfo: string;
    patientId: string;
  }): Promise<ApiResponse<CreateCaseResponse>> {
    // Transform legacy data to new format
    const caseData = {
      images: data.imageUrls.map(url => ({ url })),
      age: 30, // Default age - should be provided by frontend
      sex: 'Male' as const, // Default sex - should be provided by frontend
      doctorDiagnosis: 'Initial Assessment',
      doctorNote: data.clinicalInfo,
      patientHistory: '',
      symptoms: []
    };
    
    return this.createCase(caseData);
  }
}
